import React, { useState } from "react";
import { auth, db } from "../../firebase/config";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import { trackDailyRollup } from "../../utils/lowCostAnalytics";

const REPORT_REASONS = [
  "Spam or scam",
  "Unsafe or suspicious",
  "Fake listing",
  "Harassment or abusive behavior",
  "Wrong information",
  "Other",
];

const ReportButton = ({
  type = "general",
  targetId,
  targetOwnerId = null,
  targetCollection = "",
  reportedUserId = null,
  targetTitle = "",
  buttonLabel = "Report",
  compact = false,
  requireTrusted = false,
  userProfile = null,
  extraData = {},
  successMessage = "Report submitted. Thank you for helping keep MyFarmingPal safe.",
}) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const user = auth.currentUser;

  const closeModal = () => {
    setOpen(false);
    setReason(REPORT_REASONS[0]);
    setDetails("");
    setSubmitting(false);
  };

  const submitReport = async () => {
    if (!user) {
      alert("Please log in to report something.");
      return;
    }

    if (requireTrusted && !requireVerifiedEmail(user, userProfile)) return;

    if (!targetId) {
      alert("Missing report target.");
      return;
    }

    try {
      setSubmitting(true);

      const existingQuery = query(
        collection(db, "supportRequests"),
        where("reporterId", "==", user.uid),
        where("targetId", "==", targetId),
        limit(10)
      );
      const existingSnapshot = await getDocs(existingQuery);
      const alreadyReported = existingSnapshot.docs.some((docSnap) => {
        const data = docSnap.data();
        return (data.type || data.reportType) === type;
      });

      if (alreadyReported) {
        alert("You've already reported this item. Thanks for helping us review it.");
        closeModal();
        return;
      }

      await addDoc(collection(db, "supportRequests"), {
        requestKind: "report",
        reportType: type,
        type,
        targetType: type,
        targetId,
        targetCollection,
        targetOwnerId,
        reportedUserId: reportedUserId || targetOwnerId || null,
        targetTitle,
        reporterId: user.uid,
        reporterEmail: user.email || "",
        reason,
        details: details.trim(),
        status: "open",
        source: "report_button",
        ...extraData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      trackDailyRollup("report_submitted");

      alert(successMessage);
      closeModal();
    } catch (error) {
      console.error("Failed submitting report:", error);
      alert("Could not submit report: " + error.message);
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={compact ? "report-btn compact" : "report-btn"}
        onClick={() => setOpen(true)}
      >
        🚩 {buttonLabel}
      </button>

      {open && (
        <div className="report-modal-backdrop" onClick={closeModal}>
          <div
            className="report-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="report-modal-header">
              <div>
                <p className="eyebrow">Safety Report</p>
                <h3>Report this {type}</h3>
              </div>

              <button
                type="button"
                className="report-modal-close"
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            {targetTitle && (
              <p className="report-target">
                <strong>Item:</strong> {targetTitle}
              </p>
            )}

            <label className="form-label">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REPORT_REASONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label className="form-label">Details optional</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              placeholder="Tell us what looks wrong, unsafe, fake, or suspicious..."
            />

            <div className="report-modal-actions">
              <button
                type="button"
                className="btn-soft"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn-primary"
                onClick={submitReport}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportButton;
